<template>
  <div class="store_list_page">
    <!-- 頁面最上方 OKShop logo（點擊導向官網） -->
    <div class="logo_bar">
      <img :src="okshopLogo" alt="OKShop" class="logo_img" @click="goOkShop" />
    </div>

    <!-- 驗證中 -->
    <div v-if="status === 'verifying'" class="loading-container">
      <div class="spinner"></div>
      <p class="loading-text">資料載入中...</p>
    </div>

    <!-- 登入畫面（自動登入失敗五次後） -->
    <div v-else-if="status === 'login'" class="login_container">
      <button class="login_btn" @click="manualLogin">使用LINE登入</button>
      <div class="row_center_center mt-4 text-sm tracking-wider">
        <p style="color: #666666">若您選擇繼續，即表示您同意</p>
        <a href="https://www.okshop.tw/privacy/" target="_blank" style="color: #fd5001">條款與條件</a>
      </div>
    </div>

    <!-- 店家清單 -->
    <div
      v-else
      v-infinite-scroll="load"
      :infinite-scroll-distance="100"
      :infinite-scroll-immediate="false"
      class="list_container"
    >
      <div v-if="storeList.length === 0" class="empty_text">目前尚未加入任何店家</div>
      <div v-for="item in storeList" :key="item.store_code" class="store_card">
        <!-- 左側圓形店面圖示 -->
        <div class="store_icon">
          <img :src="storeIcon" alt="店面" class="store_icon_img" />
        </div>
        <!-- 右側內容 -->
        <div class="store_main">
          <div class="store_name">{{ item.name }}</div>
          <div class="card_actions">
            <button class="vip_btn" @click="goVip(item.store_code)">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff"><path d="M3 7l4 4 5-6 5 6 4-4-2 12H5z" /></svg>
              VIP卡
            </button>
            <button
              class="line_btn"
              :disabled="!item.line_official_address"
              @click="goLineOfficial(item.line_official_address)"
            >
              <img :src="lineIcon" alt="LINE" class="line_badge" />
              店家LINE@
            </button>
          </div>
        </div>
      </div>
      <div v-if="storeList.length > 0 && !hasMore" class="row_center_center w-full px-[5%]">
        <el-divider style="border-top: 1px solid #aaaaaa; color: #aaaaaa">沒有更多店家</el-divider>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import auth from '@/utils/auth.utils';
import commonUtils from '@/utils/common.utils';
import { verifyLineAccessToken } from '@/services/lineApi.service';
import apiData from '@/services/apiData';
import okshopLogo from '@/assets/okshop_logo.png';
import lineIcon from '@/assets/line_icon.png';
import storeIcon from '@/assets/store_icon.png';

const ATTEMPT_KEY = 'storeListLoginAttempts';
const MAX_ATTEMPTS = 5;

export default {
  data() {
    return {
      status: 'verifying',
      okshopLogo,
      lineIcon,
      storeIcon,
      isLoadingMore: false,
    };
  },
  computed: {
    ...mapGetters('storeList', { storeList: 'storeList', hasMore: 'hasMoreStores' }),
    ...mapGetters('line', { lineAccessToken: 'accessToken' }),
  },
  async created() {
    await this.init();
  },
  methods: {
    getAttempts() {
      return Number(sessionStorage.getItem(ATTEMPT_KEY) || 0);
    },
    incrementAttempts() {
      sessionStorage.setItem(ATTEMPT_KEY, String(this.getAttempts() + 1));
    },
    resetAttempts() {
      sessionStorage.removeItem(ATTEMPT_KEY);
    },
    async init() {
      // 1. 驗證 LINE token 是否有效
      let valid = false;
      if (this.lineAccessToken) {
        try {
          valid = await verifyLineAccessToken(this.lineAccessToken);
        } catch (e) {
          valid = false;
        }
      }

      if (valid) {
        this.resetAttempts();
        // 保留好友檢查（非好友會跳轉加好友頁並回傳 false）
        const isFriend = await auth.ensureOkShopFriendship();
        if (!isFriend) return;
        await this.loadStores();
        return;
      }

      // 2. 無有效 token → 走自動登入流程
      this.triggerAutoLogin();
    },
    // 自動登入：失敗五次顯示登入畫面，否則自動重新登入
    triggerAutoLogin() {
      if (this.getAttempts() >= MAX_ATTEMPTS) {
        this.status = 'login';
        return;
      }
      this.incrementAttempts();
      this.lineLogin();
    },
    async loadStores() {
      try {
        // 重新進頁從第 0 頁開始
        this.$store.commit('storeList/resetStores');
        await this.$store.dispatch('storeList/fetchMemberStores');
        this.status = 'list';
      } catch (e) {
        // API 回 200 但 IdToken expired 時，line_id_token 已過期 → 觸發自動重新登入
        console.error('fetch member stores failed', e);
        this.triggerAutoLogin();
      }
    },
    async load() {
      // 滾到底時續撈下一頁（無更多或請求進行中則略過，避免重複打 API）
      if (!this.hasMore || this.isLoadingMore) return;
      this.isLoadingMore = true;
      try {
        commonUtils.createLoading('資料載入中', '請稍後');
        await this.$store.dispatch('storeList/fetchMemberStores');
        commonUtils.closeLoading();
      } catch (e) {
        console.error('fetch more member stores failed', e);
        commonUtils.closeLoading();
      } finally {
        this.isLoadingMore = false;
      }
    },
    lineLogin() {
      // 登入前清除快取，確保登入後拿到最新店家列表
      this.$store.commit('storeList/resetStores');
      const data = new apiData();
      data.page = 'store-list';
      data.store = null;
      this.$store.dispatch('line/login', data);
    },
    manualLogin() {
      this.resetAttempts();
      this.lineLogin();
    },
    goVip(storeCode) {
      this.$router.push({ path: `/${storeCode}` });
    },
    goLineOfficial(address) {
      if (!address) return;
      window.location.href = address;
    },
    goOkShop() {
      window.location.href = 'https://www.okshop.tw/';
    },
  },
};
</script>

<style lang="scss" scoped>
.store_list_page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #fbe6cf 0%, #fdf1e3 35%, #fdf7f0 100%);
}

.logo_bar { flex: none; width: 100%; display: flex; justify-content: center; align-items: center; padding: 16px; background: #fbe6cf; }
.logo_img { width: 72%; max-width: 340px; height: auto; cursor: pointer; }

.loading-container { min-height: 60vh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; }
.spinner { width: 48px; height: 48px; border: 4px solid #f5e2cf; border-top-color: #fd5001; border-radius: 50%; animation: spin 0.8s linear infinite; }
.loading-text { margin-top: 16px; color: #666; font-size: 16px; letter-spacing: 0.64px; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.login_container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; }
.login_btn { background-color: #06c755; border: none; width: 280px; height: 60px; border-radius: 6px; color: #fff; font-weight: 500; font-size: 20px; box-shadow: 0px 3px 6px #00000029; }

.list_container { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 20px; }
.empty_text { text-align: center; color: #999; margin-top: 40px; }

.store_card {
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: #fff;
  border-radius: 18px;
  padding: 20px 18px;
  margin-bottom: 16px;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
}
.store_icon {
  flex: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #fdeede;
  display: flex;
  align-items: center;
  justify-content: center;
}
.store_icon_img { width: 50px; height: 50px; object-fit: contain; }
.store_main { flex: 1; min-width: 0; }
.store_name { font-size: 22px; font-weight: 700; color: #2b2b2b; margin-bottom: 12px; }

.card_actions { display: flex; gap: 10px; }
.vip_btn,
.line_btn {
  flex: 1;
  height: 52px;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}
.vip_btn { background: linear-gradient(135deg, #ffa033, #fd6b00); }
.line_btn { background-color: #06c755; }
.line_badge { width: 24px; height: 24px; object-fit: contain; }
.line_btn:disabled { background-color: #e0e0e0; color: #9e9e9e; cursor: not-allowed; }
.line_btn:disabled .line_badge { opacity: 0.5; }

@media (max-width: 400px) {
  .line_btn { font-size: 14px; }
}

@media (max-width: 360px) {
  .line_btn { gap: 0; padding: 0 8px; }
}
</style>
